import * as THREE from 'three';
import { BLACK, emis, std } from '../materials.ts';
import { M, V, glowSprite } from '../prims.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * Las Fauces (La Orilla 3, docs/SAGAS.md): a fish of ink the size of a ship, at the water's edge at the tip of the fin.
 * Only its head breaks the surface: a vast blunt skull of black, wet hide with an oily violet sheen, a ridge of
 * spines down the back that sinks into the sea behind, two small pale eyes high on the head, and a gaping jaw full of
 * needle teeth, upper and lower, the gullet dark. From the crown rises the root of the stalk that holds its light
 * (the stalk itself and its bulb are drawn by the coast's layer and the `lure` model). Dark (`dark`, its light
 * broken): the eyes dim and the jaw hangs shut, still. Faces +Z (the land); the rest of it is under the water at -Z.
 */

export const maw = (o?: BaseOpts & { dark?: boolean }): Model => {
  const c = begin(o, 1.4, 151);
  const { g, R } = c;
  const dark = !!o?.dark;
  const hide = std('#0b0a12', { roughness: 0.18, metalness: 0.32, bumpScale: 0.06 });
  const hideD = std('#06050a', { roughness: 0.26, metalness: 0.25, bumpScale: 0.07 });
  const sheen = std('#2c2140', { roughness: 0.15, metalness: 0.45 });
  const tooth = std('#d6d0c0', { roughness: 0.45 });
  const gum = emis('#2a0e1a', '#4a1424', dark ? 0.2 : 0.55);
  const eye = emis('#cfe8c8', '#bff0c0', dark ? 0.08 : 1.1);
  const sphere = new THREE.SphereGeometry(0.5, 18, 12);
  // Most of it is under the water: the whole head sits low and back, only the jaw at the edge.
  g.position.set(0, -0.36, -0.45);

  // The skull: a broad, blunt dome sloping back into the water.
  const skull = M(sphere, hide, g);
  skull.position.set(0, 0.42, -0.25);
  skull.scale.set(1.9, 1.05, 2.4);
  // The brow over the jaw and the cheeks, flaring wide.
  for (const k of [-1, 1]) {
    const cheek = M(sphere, hideD, g);
    cheek.position.set(k * 0.72, 0.28, 0.25);
    cheek.scale.set(0.8, 0.62, 1.1);
  }
  const brow = M(sphere, hide, g);
  brow.position.set(0, 0.66, 0.38);
  brow.scale.set(1.5, 0.42, 0.9);
  // An oily sheen streak down the crown and a ridge of spines sinking into the sea behind.
  M(new THREE.BoxGeometry(0.1, 0.05, 1.8), sheen, g).position.set(0, 0.95, -0.4);
  for (let i = 0; i < 7; i++) {
    const s = M(new THREE.ConeGeometry(0.07 + 0.02 * (i % 2), 0.36 - i * 0.03, 5), hideD, g);
    s.position.set(0, 0.96 - i * 0.09, -0.35 - i * 0.28);
    s.rotation.x = -0.55;
  }
  // The eyes, small and high, pale; dim when its light is out.
  for (const k of [-1, 1]) {
    const e = M(new THREE.SphereGeometry(0.11, 10, 8), eye, g);
    e.position.set(k * 0.55, 0.78, 0.45);
    if (!dark) glowSprite(g, '#c8f0c0', 0.28, V(k * 0.56, 0.8, 0.5), 0.55);
  }
  // The jaw: the upper lip over a gaping mouth, the lower jaw hanging (shut when dark), needle teeth on both.
  const jaw = new THREE.Group();
  jaw.position.set(0, 0.1, 0.55);
  g.add(jaw);
  const lower = M(sphere, hideD, jaw);
  lower.scale.set(1.45, 0.32, 0.95);
  lower.position.set(0, -0.08, 0.15);
  const gullet = M(new THREE.CircleGeometry(0.42, 18), BLACK, g);
  gullet.position.set(0, 0.28, 0.82);
  gullet.scale.set(1.4, 0.55, 1);
  const lip = M(new THREE.TorusGeometry(0.5, 0.07, 6, 22, Math.PI), gum, g);
  lip.position.set(0, 0.3, 0.8);
  lip.scale.set(1.35, 0.5, 1);
  // Needle teeth: a long uneven row along the upper lip, pointing down, and up from the lower jaw.
  const needle = new THREE.ConeGeometry(0.04, 0.32, 4);
  for (let i = 0; i < 17; i++) {
    const a = Math.PI * (0.06 + (0.88 * i) / 16);
    const up = M(needle, tooth, g);
    up.position.set(Math.cos(a) * 0.66, 0.3 + Math.sin(a) * 0.14, 0.86 - Math.sin(a) * 0.05);
    up.rotation.set(Math.PI, 0, R() * 0.4);
    up.scale.y = 0.7 + 0.6 * Math.abs(Math.sin(i * 1.7));
    const dn = M(needle, tooth, jaw);
    dn.position.set(Math.cos(a) * 0.6, 0.06, 0.48 - Math.sin(a) * 0.12);
    dn.rotation.z = R() * 0.4;
    dn.scale.y = 0.6 + 0.5 * Math.abs(Math.cos(i * 1.3));
  }
  // Pale old scars across the crown, so its shape reads from above.
  const scar = std('#3a3448', { roughness: 0.4, metalness: 0.2 });
  for (let i = 0; i < 5; i++) {
    const sc = M(new THREE.BoxGeometry(0.5 + 0.3 * Math.abs(R()), 0.02, 0.05), scar, g);
    sc.position.set(R() * 0.9, 0.9 - Math.abs(R()) * 0.12, -0.2 + R() * 1.4);
    sc.rotation.y = R() * 1.2;
  }
  // The stalk's root at the crown: a fleshy knob, a little bent forward.
  const root = M(new THREE.CylinderGeometry(0.06, 0.12, 0.5, 8), hide, g);
  root.position.set(0, 1.12, 0.2);
  root.rotation.x = 0.35;
  // Ink running off it into the sea, a dark sheen at the waterline.
  const wet = std('#141026', { roughness: 0.05, metalness: 0.4, transparent: true, opacity: 0.7, depthWrite: false });
  const pool = M(new THREE.CircleGeometry(1.5, 22), wet, g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(0, 0.03, -0.2);
  pool.scale.set(1.25, 1, 1);
  if (!dark) glowSprite(g, '#3a1a40', 1.6, V(0, 0.4, 0.9), 0.25);
  // Breathing: the jaw works open and shut, slowly; dark, it hangs still.
  const tick = (n: number): void => {
    jaw.rotation.x = dark ? 0.02 : 0.32 + 0.12 * Math.sin(n * (Math.PI / 2));
    skull.scale.y = 1.05 + (dark ? 0 : 0.025 * Math.sin(n * (Math.PI / 2) + 1));
  };
  tick(0);
  return done(c, 1.3, tick);
};

/*
 * The Maw's light (La Orilla 3): the bulb at the end of its stalk, hanging over the way to its mouth. A swollen
 * translucent pod giving off a cold, sickly light, pale green-white, with thin filaments hanging from it. The stalk
 * arching back to the Maw's crown is the coast layer's; this is what is struck to break it.
 */
export const lure = (o?: BaseOpts): Model => {
  const c = begin(o, 0.4, 152);
  const { g, R } = c;
  const pod = emis('#d8f4d0', '#c8ffd0', 1.6, { transparent: true, opacity: 0.92 });
  const skin = std('#1a1424', { roughness: 0.25, metalness: 0.3 });
  const fil = emis('#7fb89a', '#9fe8b8', 0.6);
  const y = 1.55;
  // The stalk's last stretch, bending down into the bulb (the layer's arch meets it at the top).
  const end = M(new THREE.CylinderGeometry(0.035, 0.05, 0.45, 6), skin, g);
  end.position.set(0, y + 0.42, -0.05);
  end.rotation.x = -0.25;
  const bulb = M(new THREE.SphereGeometry(0.2, 14, 10), pod, g);
  bulb.position.set(0, y, 0);
  bulb.scale.set(1, 1.2, 1);
  // A dark collar where the bulb meets the stalk.
  M(new THREE.TorusGeometry(0.11, 0.035, 5, 12), skin, g).position.set(0, y + 0.2, 0);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const f = M(new THREE.CylinderGeometry(0.006, 0.004, 0.45 + 0.2 * R(), 3), fil, g);
    f.position.set(Math.cos(a) * 0.11, y - 0.4, Math.sin(a) * 0.11);
    f.rotation.set(R() * 0.3, 0, R() * 0.3);
  }
  glowSprite(g, '#cfffd8', 1.4, V(0, y, 0), 0.75);
  glowSprite(g, '#9fe8b8', 3.2, V(0, y, 0), 0.22);
  const tick = (n: number): void => {
    bulb.scale.set(1 + 0.06 * Math.sin(n * (Math.PI / 2)), 1.2 + 0.06 * Math.sin(n * (Math.PI / 2)), 1);
    g.rotation.z = 0.05 * Math.sin(n * (Math.PI / 2) + 0.5);
  };
  tick(0);
  return done(c, 2.0, tick);
};
